import { Copy } from "lucide-react";
import { IMAGE_BACKGROUND_PRESETS, normalizeHexColor } from "@gaole/shared";
import { toast } from "../stores/toast";
import { Button, ColorField } from "./ui";

type ThumbnailBackgroundMode = "default" | "white" | "black" | "custom";

function modeFromValue(value: string): ThumbnailBackgroundMode {
  if (!value) return "default";
  const normalized = normalizeHexColor(value);
  if (!normalized) return "custom";
  if (normalized === IMAGE_BACKGROUND_PRESETS.white) return "white";
  if (normalized === IMAGE_BACKGROUND_PRESETS.black) return "black";
  return "custom";
}

export function ThumbnailBackgroundField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const mode = modeFromValue(value);
  const customHex = normalizeHexColor(value) ?? IMAGE_BACKGROUND_PRESETS.white;

  function setMode(next: ThumbnailBackgroundMode) {
    if (next === "default") onChange("");
    else if (next === "white") onChange(IMAGE_BACKGROUND_PRESETS.white);
    else if (next === "black") onChange(IMAGE_BACKGROUND_PRESETS.black);
    else onChange(customHex);
  }

  async function copyHex() {
    const hex = normalizeHexColor(value);
    if (!hex) {
      toast("info", "No custom color set — pick White, Black, or Custom first");
      return;
    }
    try {
      await navigator.clipboard.writeText(hex);
      toast("success", `Copied ${hex.toUpperCase()}`);
    } catch {
      toast("error", "Could not copy color");
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-foreground">Thumbnail background</p>
      <p className="text-[11px] text-muted">
        Behind product photos on shop cards. Use white or black for sealed products, or pick a custom hex.
      </p>
      <div className="flex flex-wrap gap-2">
        {(
          [
            ["default", "Default"],
            ["white", "White"],
            ["black", "Black"],
            ["custom", "Custom"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              mode === key
                ? "border-brand-pink/50 bg-brand-pink/10 text-foreground"
                : "border-line bg-hover text-muted hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
        <Button type="button" variant="ghost" size="sm" className="inline-flex items-center gap-1" onClick={copyHex}>
          <Copy size={13} aria-hidden />
          Copy hex
        </Button>
      </div>
      {mode === "custom" && (
        <ColorField
          label="Custom color"
          hint="6-digit hex, e.g. #f5f5f5"
          value={customHex}
          onChange={(hex) => onChange(normalizeHexColor(hex) ?? hex)}
        />
      )}
      {mode !== "default" && mode !== "custom" && (
        <div
          className="flex h-10 items-center justify-center rounded-xl border border-line text-[11px] font-mono uppercase text-muted"
          style={{ backgroundColor: value }}
        >
          {value}
        </div>
      )}
    </div>
  );
}
